// Terms of Service for subswall.com operated by Dr. Gotthilf LLC (Wyoming).
// Plain-English, Stripe-onboarding-grade. Not a substitute for legal advice.

export default function Terms(): JSX.Element {
  return (
    <article className="legal-page">
      <h1>Terms of Service</h1>
      <p className="legal-meta">
        Effective date: 2026-05-30 · Last updated: 2026-05-30
      </p>

      <p>
        <em>
          This document is the publicly-posted terms of service for SubsWall.
          It is not legal advice for you. If you operate a business that uses the
          Platform, you should consult your own lawyer about how these Terms
          apply to you.
        </em>
      </p>

      <h2>Table of contents</h2>
      <ul>
        <li><a href="#about">1. About these Terms</a></li>
        <li><a href="#defs">2. Definitions</a></li>
        <li><a href="#eligibility">3. Eligibility</a></li>
        <li><a href="#accounts">4. Accounts and authentication</a></li>
        <li><a href="#creator">5. Creator obligations</a></li>
        <li><a href="#donor">6. Donor obligations</a></li>
        <li><a href="#payments">7. Payments, fees, and Stripe</a></li>
        <li><a href="#refunds">8. Refunds and chargebacks</a></li>
        <li><a href="#prohibited">9. Prohibited uses</a></li>
        <li><a href="#suspension">10. Suspension and termination</a></li>
        <li><a href="#ip">11. Intellectual property</a></li>
        <li><a href="#disclaimers">12. Disclaimers</a></li>
        <li><a href="#liability">13. Limitation of liability</a></li>
        <li><a href="#indemnity">14. Indemnification</a></li>
        <li><a href="#disputes">15. Governing law and disputes</a></li>
        <li><a href="#changes">16. Changes to these Terms</a></li>
        <li><a href="#misc">17. Miscellaneous</a></li>
        <li><a href="#contact">18. Contact</a></li>
      </ul>

      <h2 id="about">1. About these Terms</h2>
      <p>
        These Terms of Service (the <strong>"Terms"</strong>) form a binding
        contract between you and <strong>Dr. Gotthilf LLC</strong>, a Wyoming
        limited liability company with its registered office at 30 N Gould St
        Ste R, Sheridan, WY 82801, United States (<strong>"Company"</strong>,
        <strong>"we"</strong>, <strong>"us"</strong>, or <strong>"our"</strong>),
        which operates the <strong>SubsWall</strong> service available at{" "}
        <a href="https://subswall.com">https://subswall.com</a> (the{" "}
        <strong>"Platform"</strong>). By accessing or using the Platform, by
        creating an account, by sending a Donation, or by clicking a button
        indicating acceptance, you agree to be bound by these Terms. If you do
        not agree, do not use the Platform.
      </p>

      <h2 id="defs">2. Definitions</h2>
      <ul>
        <li><strong>"Platform"</strong> means the SubsWall website, overlay,
          APIs, dashboards, and related software operated by the Company.</li>
        <li><strong>"Creator"</strong> means a person or entity that creates a
          Creator account on the Platform to receive Donations from their
          audience.</li>
        <li><strong>"Donor"</strong> means a person who initiates a payment to
          a Creator through the Platform.</li>
        <li><strong>"Donation"</strong> means a voluntary one-time payment sent
          by a Donor to a Creator through the Platform. Donations are tips, not
          payment for goods or services.</li>
        <li><strong>"Stripe"</strong> means Stripe, Inc. and its affiliates,
          the licensed payment processor that handles all card processing and
          payout settlement for the Platform.</li>
        <li><strong>"Connected Account"</strong> means a Creator's Stripe
          Connect Express account used to receive Donation payouts.</li>
      </ul>

      <h2 id="eligibility">3. Eligibility</h2>
      <p>
        You must be at least 18 years old (or the age of majority in your
        jurisdiction, whichever is higher) to use the Platform. You must not be
        located in, organized under the laws of, or ordinarily resident in any
        country or region subject to comprehensive U.S. sanctions (currently
        including Cuba, Iran, North Korea, Syria, the Crimea, Donetsk, and
        Luhansk regions of Ukraine), and you must not appear on any U.S.
        government list of restricted or prohibited parties (including OFAC
        SDN). By using the Platform, you represent and warrant that you meet
        these requirements.
      </p>

      <h2 id="accounts">4. Accounts and authentication</h2>
      <p>
        Creators sign in via Twitch or Google OAuth. You are responsible for
        all activity on your account and for safeguarding your authentication
        credentials. You agree to notify us promptly at{" "}
        <a href="mailto:support@subswall.com">support@subswall.com</a> of any
        suspected unauthorized access. You may delete your account from
        Settings; deletion is subject to the data-retention rules in our{" "}
        <a href="/privacy">Privacy Policy</a> and the financial-records rules
        in section 7 below.
      </p>

      <h2 id="creator">5. Creator obligations</h2>
      <ul>
        <li>
          Provide true, complete, and current information when creating a
          Connected Account with Stripe, including your legal name, address,
          date of birth, tax identifier, and bank or card-payout details where
          required.
        </li>
        <li>
          Comply with all laws and tax obligations that apply to the Donations
          you receive. The Company does not issue tax forms to Creators except
          where required by law; Stripe may issue forms (such as IRS Form
          1099-K) where its thresholds are met.
        </li>
        <li>
          Do not solicit Donations in exchange for promised goods or services,
          investment returns, sweepstakes entries, or anything that would
          re-characterize a tip as a sale, security, or wager.
        </li>
        <li>
          Do not engage in self-funding (donating to yourself or arranging for
          others to do so) to inflate metrics, evade fees, launder funds, or
          test card numbers.
        </li>
        <li>
          Keep your contact information current so Stripe and the Company can
          reach you about your Connected Account.
        </li>
      </ul>

      <h2 id="donor">6. Donor obligations</h2>
      <ul>
        <li>You may only use a payment method that you are legally authorized
          to use. Using a card without the cardholder's permission is fraud and
          will be reported.</li>
        <li>Donations are voluntary tips. They are non-refundable except as
          described in our <a href="/refunds">Refund Policy</a>.</li>
        <li>You agree not to abuse the chargeback process. If you have a
          dispute, contact <a href="mailto:support@subswall.com">support@subswall.com</a>{" "}
          before initiating a chargeback with your card issuer.</li>
      </ul>

      <h2 id="payments">7. Payments, fees, and Stripe</h2>
      <p>
        All card processing and payouts on the Platform are performed by{" "}
        <strong>Stripe, Inc.</strong>, a U.S.-licensed money transmitter.
        <strong> The Company is a software platform, not a money transmitter
        or financial institution.</strong> The Company never takes custody of
        Donor funds.
      </p>
      <p>
        By using the Platform, Creators also agree to the{" "}
        <a href="https://stripe.com/connect-account/legal/full" target="_blank" rel="noopener noreferrer">
          Stripe Connected Account Agreement
        </a>{" "}
        and the{" "}
        <a href="https://stripe.com/legal/ssa" target="_blank" rel="noopener noreferrer">
          Stripe Services Agreement
        </a>, and Donors agree to the{" "}
        <a href="https://stripe.com/legal/end-users" target="_blank" rel="noopener noreferrer">
          Stripe End User Terms
        </a>.
      </p>
      <p>
        <strong>Fees.</strong> The Company charges a platform application fee
        of <strong>4.9% (four point nine percent)</strong> on each Donation, which is
        deducted from the Donation before payout to the Creator. In addition,
        Stripe charges its own payment-processing fees, typically{" "}
        <strong>2.9% + $0.30 USD</strong> per successful card charge (rates
        vary by currency, card type, and country and are set by Stripe). Stripe
        processing fees are borne by the Creator and are deducted by Stripe
        from the destination charge. The Company may change its platform fee
        prospectively by giving notice on the Platform; existing scheduled
        payouts will not be retroactively re-priced.
      </p>
      <p>
        <strong>Payouts.</strong> Stripe pays Creators on the schedule
        configured in their Connected Account (typically daily or weekly, after
        Stripe's standard rolling reserve, if any). The Company does not
        control payout timing.
      </p>
      <p>
        <strong>Tax.</strong> Creators are solely responsible for reporting and
        paying any income tax, VAT, GST, or other taxes that apply to
        Donations they receive.
      </p>

      <h2 id="refunds">8. Refunds and chargebacks</h2>
      <p>
        Donations are voluntary tips and are generally non-refundable.
        Discretionary refunds, the streamer-refund process, and the chargeback
        policy are described in our separate{" "}
        <a href="/refunds">Refund &amp; Chargeback Policy</a>, which is
        incorporated into these Terms by reference. In summary: chargebacks
        and refunds are debited from the Creator's Connected Account, not from
        the Company, consistent with Stripe Connect's destination-charge
        architecture.
      </p>

      <h2 id="prohibited">9. Prohibited uses</h2>
      <p>
        The Platform may not be used by or for businesses or activities listed
        in Stripe's restricted businesses policy or any of the following,
        without limitation:
      </p>
      <ul>
        <li>Gambling, lotteries, sweepstakes for value, sports betting, or any
          activity where Donations are tied to a chance to win.</li>
        <li>Adult content, sexual services, escort services, or pornography.</li>
        <li>Weapons, firearms, ammunition, explosives, or weapon parts and
          accessories.</li>
        <li>Controlled substances, illegal drugs, marijuana (including
          jurisdictions where it is locally legal but federally restricted),
          drug paraphernalia, e-cigarettes, and prescription drugs without a
          license.</li>
        <li>Hate speech, content that promotes violence against a person or
          group, terrorism, or content that exploits or endangers minors.</li>
        <li>Counterfeit goods, intellectual-property infringement, or
          unlicensed sale of branded merchandise.</li>
        <li>Fraud, deceptive practices, pyramid or Ponzi schemes,
          multi-level marketing without disclosure, get-rich-quick schemes.</li>
        <li>Money services, money transmission, money laundering, sanctions
          evasion, unregistered securities, or initial coin offerings.</li>
        <li>Any use that violates the law of the Donor's or Creator's
          jurisdiction or the law of the United States.</li>
        <li>Any use that violates Stripe's restricted businesses policy, as
          updated by Stripe from time to time.</li>
      </ul>
      <p>
        Detailed acceptable-use rules are in our{" "}
        <a href="/acceptable-use">Acceptable Use Policy</a>, incorporated by
        reference.
      </p>

      <h2 id="suspension">10. Suspension and termination</h2>
      <p>
        We may suspend, restrict, hold funds in, or terminate any account at
        any time, with or without notice, if we believe in good faith that
        continued use poses risk of fraud, chargeback, sanctions, legal
        liability, or violation of these Terms. Stripe may independently
        suspend or terminate a Connected Account under its own agreements;
        in that case we will be unable to process Donations to the affected
        Creator. You may terminate your account at any time by deleting it
        from Settings or emailing{" "}
        <a href="mailto:support@subswall.com">support@subswall.com</a>.
      </p>

      <h2 id="ip">11. Intellectual property</h2>
      <p>
        <strong>Your content.</strong> You retain ownership of the content you
        post (display name, avatar, Donation messages, faction selections,
        overlay branding, AI-character prompts). You grant the Company a
        worldwide, royalty-free, non-exclusive, perpetual license to host,
        store, transmit, display, and distribute that content as necessary to
        operate the Platform (for example, showing a Donation on a Creator's
        overlay or supporter page).
      </p>
      <p>
        <strong>AI-generated characters.</strong> When you provide a prompt
        for an AI-generated character image (Imagen, Nano Banana, or successor
        models) or an AI-synthesized voice line via SubsWall, you represent
        and warrant that your prompt and any resulting output (a)&nbsp;do not
        infringe any third party's copyright, trademark, right of publicity,
        moral right, or other intellectual property right, and (b)&nbsp;do not
        depict a real, identifiable person without that person's consent, and
        (c)&nbsp;do not violate the AI provider's acceptable use policy. You
        agree the Company may remove, refuse to generate, or replace any
        AI-generated character at our sole discretion, with or without notice,
        and that you have no entitlement to any specific generated output.
        Generated images are clearly labeled as AI-generated on the Platform
        in accordance with the EU AI Act and similar disclosure regimes.
      </p>
      <p>
        <strong>Our platform.</strong> The Company owns the Platform's
        software, the "SubsWall" name and logo, the wall mechanic, the
        faction mechanic, the overlay design, and all other aspects of the
        Platform other than your content and any third-party content. You may
        not copy, modify, reverse engineer, or create derivative works of the
        Platform except as expressly permitted by law.
      </p>

      <h2 id="disclaimers">12. Disclaimers</h2>
      <p>
        <strong>
          THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
          WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY,
          INCLUDING WITHOUT LIMITATION ANY WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY,
          UNINTERRUPTED OPERATION, OR ERROR-FREE OPERATION.
        </strong>{" "}
        We do not warrant that any particular Donation will be successfully
        processed, that the overlay will display in real time without delay,
        or that the Platform will be available at any given moment.
      </p>

      <h2 id="liability">13. Limitation of liability</h2>
      <p>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY'S AGGREGATE
          LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING OUT OF OR RELATING
          TO THE PLATFORM OR THESE TERMS IS LIMITED TO THE GREATER OF (A) THE
          TOTAL FEES YOU PAID TO THE COMPANY (NOT TO STRIPE) IN THE 12 MONTHS
          IMMEDIATELY PRECEDING THE CLAIM, OR (B) ONE HUNDRED U.S. DOLLARS
          ($100 USD).
        </strong>
      </p>
      <p>
        <strong>
          IN NO EVENT WILL THE COMPANY BE LIABLE FOR INDIRECT, INCIDENTAL,
          SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR LOST
          PROFITS, LOST REVENUE, LOST DATA, LOSS OF GOODWILL, OR COST OF
          SUBSTITUTE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES.
        </strong>
      </p>
      <p>
        Some jurisdictions do not allow the exclusion or limitation of certain
        damages; in those jurisdictions our liability is limited to the
        greatest extent permitted by law.
      </p>

      <h2 id="indemnity">14. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless the Company, its
        officers, members, employees, contractors, and agents from and against
        any and all claims, demands, losses, liabilities, damages, judgments,
        fines, costs, and expenses (including reasonable attorneys' fees)
        arising out of or relating to: (a) your use of the Platform; (b) your
        breach of these Terms or any policy incorporated by reference; (c)
        your violation of any law or the rights of any third party; (d) any
        content you submit through the Platform, including any AI-character
        prompt, AI-generated image, AI-synthesized voice line, or Donation
        message that infringes any intellectual property right, right of
        publicity, or similar right of a third party; or (e) any chargeback,
        refund, or dispute initiated on a Donation you submitted or received.
      </p>

      <h2 id="disputes">15. Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Wyoming, United
        States, without regard to its conflict-of-laws principles, and the
        federal laws of the United States that apply. The U.N. Convention on
        Contracts for the International Sale of Goods does not apply.
      </p>
      <p>
        <strong>Mandatory arbitration.</strong> Except for claims for
        injunctive relief related to intellectual property or unauthorized
        access to the Platform, any dispute, claim, or controversy arising out
        of or relating to these Terms or the Platform will be resolved by
        binding individual arbitration administered by{" "}
        <strong>JAMS</strong> under its then-current Comprehensive Arbitration
        Rules and Procedures (or, where the amount in controversy is below the
        JAMS streamlined threshold, the Streamlined Arbitration Rules). The
        seat of arbitration will be <strong>Sheridan, Wyoming</strong>, and
        the arbitration may be conducted remotely. The arbitrator's award is
        final and may be entered in any court of competent jurisdiction.
      </p>
      <p>
        <strong>Class-action waiver.</strong> You and the Company each agree
        to bring claims against the other only in your individual capacity,
        and not as a plaintiff or class member in any purported class,
        collective, or representative action.
      </p>

      <h2 id="changes">16. Changes to these Terms</h2>
      <p>
        We may modify these Terms from time to time. If we make a material
        change, we will provide notice by email (to the address on your
        account) and by posting the updated Terms on the Platform with a new
        effective date. Changes take effect on the new effective date.
        Continued use of the Platform after the new effective date constitutes
        acceptance.
      </p>

      <h2 id="misc">17. Miscellaneous</h2>
      <ul>
        <li>
          <strong>Severability.</strong> If any provision of these Terms is
          held unenforceable, the remaining provisions remain in full force,
          and the unenforceable provision will be modified to the minimum
          extent necessary to be enforceable.
        </li>
        <li>
          <strong>Assignment.</strong> You may not assign these Terms without
          our prior written consent. We may assign these Terms in connection
          with a merger, acquisition, or sale of assets.
        </li>
        <li>
          <strong>No waiver.</strong> Our failure to enforce any provision is
          not a waiver of that provision.
        </li>
        <li>
          <strong>Entire agreement.</strong> These Terms, together with the{" "}
          <a href="/privacy">Privacy Policy</a>, the{" "}
          <a href="/refunds">Refund Policy</a>, and the{" "}
          <a href="/acceptable-use">Acceptable Use Policy</a>, are the entire
          agreement between you and the Company regarding the Platform and
          supersede any prior agreements.
        </li>
        <li>
          <strong>Independent contractors.</strong> Nothing in these Terms
          creates an agency, partnership, joint venture, or employment
          relationship between you and the Company.
        </li>
      </ul>

      <h2 id="contact">18. Contact</h2>
      <p>
        Dr. Gotthilf LLC<br />
        30 N Gould St Ste R<br />
        Sheridan, WY 82801<br />
        United States<br />
        Email: <a href="mailto:support@subswall.com">support@subswall.com</a>
      </p>
    </article>
  );
}
